import { Injectable } from '@angular/core';
import { CurrencyRateOperationEnum } from '@enums/CurrencyRateOperation';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { Currency } from '@interfaces/Currency';
import { EntityQuery } from 'breeze-client';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BreezeBaseService } from './breeze-base.service';
import { CurrencySymbolService } from './currency-symbol.service';

@Injectable({
  providedIn: 'root'
})
export class CurrencyService extends BreezeBaseService {
  public static applyMultiplyer(amount: number, operation: CurrencyRateOperationEnum, currencyConversionRate: number) {
    if (operation === CurrencyRateOperationEnum.Multiplication) {
      return amount * currencyConversionRate;
    } else {
      // division
      if (currencyConversionRate === 0) {
        return 0;
      }
      return amount / currencyConversionRate;
    }
  }

  public static removeMultiplyer(
    amount: number,
    operation: number | CurrencyRateOperationEnum,
    currencyConversionRate: number
  ) {
    if (operation === CurrencyRateOperationEnum.Multiplication) {
      return this.applyMultiplyer(amount, CurrencyRateOperationEnum.Division, currencyConversionRate);
    }
    return this.applyMultiplyer(amount, CurrencyRateOperationEnum.Multiplication, currencyConversionRate);
  }

  public constructor(private currencySymbolService: CurrencySymbolService, private logger: NGXLogger) {
    super('currency', 'currency');
  }

  public async saveChanges() {
    if (!this.em.hasChanges()) {
      return;
    }

    return this.em.saveChanges();
  }

  private $getCompanyCurrencies: ReplaySubject<CompanyCurrency[]>;
  public getCompanyCurrencies(): Observable<CompanyCurrency[]> {
    if (!this.$getCompanyCurrencies) {
      this.getCompanyCurrenciesAsync().catch((error: Error) => {
        this.logger.error(`CurrencyService.getCompanyCurrencies(): ${error.message}`);
        throw error;
      });
    }
    return this.$getCompanyCurrencies.asObservable();
  }

  public async getCompanyCurrenciesAsync() {
    if (!this.$getCompanyCurrencies) {
      this.$getCompanyCurrencies = new ReplaySubject<CompanyCurrency[]>(1);
      const query = EntityQuery.from('CompanyCurrencies').orderBy('currencyCode');
      const currencies = await this.executeQueryResults<CompanyCurrency>(query);
      this.$getCompanyCurrencies?.next(currencies);
      return currencies;
    }
    return firstValueFrom(this.$getCompanyCurrencies);
  }

  public getCurrencyByCompanyAndCode(companyCode: string, currencyCode: string): Observable<CompanyCurrency | null> {
    return this.getCompanyCurrencies().pipe(
      map(currencies => {
        return currencies.find(c => c.currencyCode === currencyCode && c.companyCode === companyCode);
      })
    );
  }

  private $getCurrencies: ReplaySubject<Currency[]>;
  private getCurrencies() {
    if (!this.$getCurrencies) {
      this.$getCurrencies = new ReplaySubject<Currency[]>(1);

      const query = EntityQuery.from('Currencies');

      this.executeQueryResults<Currency>(query)
        .then(results => {
          const currencies = results;
          this.$getCurrencies.next(currencies);
        })
        .catch((error: Error) => {
          this.logger.error(`CurrencyService.getCurrencies(): ${error.message}`);
          throw error;
        });
    }

    return this.$getCurrencies.asObservable();
  }

  public getCurrencySymbol(currencyCode: string, setCurrent = false): Observable<Currency | null> {
    return this.getCurrencies().pipe(
      map(currencies => {
        const currency = currencies.find(c => c.code === currencyCode);

        if (currency && setCurrent) {
          this.currencySymbolService.changeCurrencySymbol(currency.code);
        }
        return currency;
      })
    );
  }

  public getFunctionalCurrency(companyCode: string) {
    return this.getCompanyCurrencies().pipe(
      map(currencies => {
        return currencies.find(c => c.isFunctional && c.companyCode === companyCode);
      })
    );
  }

  public findFunctionalCurrency(companyCode: string, companyCurrencies: CompanyCurrency[]) {
    return companyCurrencies.find(c => c.isFunctional && c.companyCode === companyCode);
  }
}
